import React, { useState } from 'react';
import { useAuth } from './AuthContext';
import './Paywall.css';

const PLANS = [
  {
    id:       'yearly',
    priceId:  'price_1TKOZvILOpB6RcrOkie5R5Kb',
    label:    'Yearly',
    price:    '$24.99',
    period:   'per year',
    monthly:  '$2.08/mo',
    badge:    'Best value — save 48%',
    featured: true,
  },
  {
    id:      'monthly',
    priceId: 'price_1TKORnILOpB6RcrOkFwJKC0e',
    label:   'Monthly',
    price:   '$3.99',
    period:  'per month',
    monthly: null,
    badge:   null,
    featured: false,
  },
];

const FEATURES = [
  { icon: '📈', label: 'Trends',           desc: 'Cumulative frequency charts across the full draw history' },
  { icon: '📊', label: 'Distribution',     desc: 'Histograms with mean, median, std dev & skewness' },
  { icon: '🔗', label: 'Number Pairs',     desc: 'Historical co-occurrence counts for every number pair' },
  { icon: '⏱',  label: 'Gap Analysis',     desc: 'Draws elapsed since each number last appeared' },
  { icon: '🎲', label: 'Smart Picker',     desc: 'Frequency-weighted and gap-based number generation' },
  { icon: '📅', label: 'Date Filters',     desc: 'Narrow the dataset to 1, 3, or 5-year windows' },
  { icon: '⚡', label: 'Both Games',       desc: 'Powerball and Mega Millions' },
];

export default function Paywall({ onSelectPlan, onClose }) {
  const { session } = useAuth();
  const [selected,      setSelected]      = useState('yearly');
  const [promoOpen,     setPromoOpen]     = useState(false);
  const [promoCode,     setPromoCode]     = useState('');
  const [promoLoading,  setPromoLoading]  = useState(false);
  const [promoResult,   setPromoResult]   = useState(null);
  const [promoError,    setPromoError]    = useState(null);

  const handleApplyPromo = async () => {
    if (!promoCode.trim()) return;
    setPromoLoading(true);
    setPromoError(null);
    try {
      const res  = await fetch('/api/validate-promo.php', {
        method:  'POST',
        headers: { 'Authorization': 'Bearer ' + session?.access_token, 'Content-Type': 'application/json' },
        body:    JSON.stringify({ code: promoCode }),
      });
      const data = await res.json();
      if (data.valid) {
        setPromoResult(data);
      } else {
        setPromoError(data.error || 'Invalid promo code.');
      }
    } catch {
      setPromoError('Could not validate code. Please try again.');
    } finally {
      setPromoLoading(false);
    }
  };

  const handleSubscribe = () => {
    const plan = PLANS.find(p => p.id === selected);
    if (plan) onSelectPlan(plan.priceId, promoResult ?? null);
  };

  return (
    <div className="pw-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="pw-modal">
        <button className="pw-close" onClick={onClose} aria-label="Close">✕</button>

        <div className="pw-header">
          <h2 className="pw-title">LottoPickle Premium</h2>
          <p className="pw-subtitle">Unlock all stats, tools, and insights</p>
        </div>

        <ul className="pw-features">
          {FEATURES.map((f) => (
            <li key={f.label} className="pw-feature">
              <span className="pw-feat-icon">{f.icon}</span>
              <span className="pw-feat-text">
                <strong>{f.label}</strong> — {f.desc}
              </span>
            </li>
          ))}
        </ul>

        <div className="pw-packages">
          {PLANS.map((plan) => (
            <button
              key={plan.id}
              className={`pw-pkg ${selected === plan.id ? 'selected' : ''} ${plan.featured ? 'featured' : ''}`}
              onClick={() => setSelected(plan.id)}
            >
              {plan.badge && <span className="pw-badge">{plan.badge}</span>}
              <span className="pw-pkg-period">{plan.label}</span>
              <span className="pw-pkg-price">
                {plan.price}
                <span className="pw-pkg-per"> / {plan.period.replace('per ', '')}</span>
              </span>
              {plan.monthly && <span className="pw-pkg-monthly">{plan.monthly} billed annually</span>}
            </button>
          ))}
        </div>

        {/* Promo code */}
        <div className="pw-promo">
          {promoResult ? (
            <div className="pw-promo-applied">
              <span>🏷 <strong>{promoResult.name}</strong> — {promoResult.percentOff ? `${promoResult.percentOff}% off` : `$${(promoResult.amountOff / 100).toFixed(2)} off`} applied</span>
              <button className="pw-promo-remove" onClick={() => { setPromoResult(null); setPromoCode(''); setPromoOpen(false); }}>✕</button>
            </div>
          ) : promoOpen ? (
            <div className="pw-promo-row">
              <input
                className="pw-promo-input"
                type="text"
                value={promoCode}
                onChange={(e) => { setPromoCode(e.target.value.toUpperCase()); setPromoError(null); }}
                placeholder="Enter promo code"
                maxLength={30}
                autoFocus
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleApplyPromo())}
              />
              <button className="pw-promo-btn" onClick={handleApplyPromo} disabled={promoLoading || !promoCode.trim()}>
                {promoLoading ? '…' : 'Apply'}
              </button>
            </div>
          ) : (
            <button className="pw-promo-toggle" onClick={() => setPromoOpen(true)}>
              Have a promo code?
            </button>
          )}
          {promoError && <p className="pw-promo-error">{promoError}</p>}
        </div>

        <button className="pw-cta" onClick={handleSubscribe}>
          Subscribe now
        </button>

        <p className="pw-legal">
          Subscriptions auto-renew until cancelled. Cancel any time in your account settings.<br />
          Plus applicable sales tax where required by law.
        </p>
      </div>
    </div>
  );
}
